/**
 * Import / Export admin sayfası — toplu içe ve dışa aktarma.
 *
 * Backend: ImportJob/ExportJob, file_format CSV/XLSX/JSON,
 * importer_name'e göre dispatch (catalog.product, recipe.bom vs.)
 */
import { Card, Stack, Table, Badge, Loader, Center, Tabs, Button, Group, Modal, Select, Switch, Text, FileInput, Progress, ScrollArea } from "@mantine/core";
import { IconUpload, IconDownload, IconRefresh, IconAlertTriangle } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useResource, useAction, apiClient } from "../../../shared/api";
import { PageHeader, EmptyState, formatDateTime } from "../../../shared/ui";

const IMPORTER_OPTIONS = [
  { value: "catalog.product", label: "Ürünler (Product)" },
  { value: "catalog.category", label: "Kategoriler (Category)" },
  { value: "catalog.modifier", label: "Modifier'lar" },
  { value: "recipe.bom", label: "Reçeteler (BOM)" },
  { value: "recipe.ingredient", label: "Hammaddeler" },
  { value: "inventory.stock_receipt", label: "Stok Girişi" },
  { value: "inventory.stock_count", label: "Stok Sayımı" },
  { value: "purchasing.supplier", label: "Tedarikçiler" },
];

const EXPORTER_OPTIONS = [
  { value: "catalog.product", label: "Ürün Listesi" },
  { value: "catalog.modifier", label: "Modifier Listesi" },
  { value: "inventory.stock_snapshot", label: "Stok Snapshot" },
  { value: "inventory.ledger", label: "Stok Hareket Ledger" },
  { value: "audit.log", label: "Audit Log" },
  { value: "pos.waiter_activity", label: "Garson Aktivite Raporu" },
  { value: "pos.waiter_commission", label: "Garson Prim Raporu" },
];

const STATUS_COLOR: Record<string, string> = {
  PENDING: "gray", RUNNING: "blue", COMPLETED: "green", FAILED: "red", CANCELLED: "orange",
};

export function ImportExportPage() {
  const [importOpen, importHandlers] = useDisclosure(false);
  const [exportOpen, exportHandlers] = useDisclosure(false);

  return (
    <Stack gap="md">
      <PageHeader
        title="Toplu İçe / Dışa Aktarma"
        subtitle="Excel/CSV ile ürün, stok, müşteri içe aktarma; raporları dışa aktarma"
        actions={
          <Group>
            <Button leftSection={<IconUpload size={16} />} onClick={importHandlers.open}>
              İçe Aktar
            </Button>
            <Button leftSection={<IconDownload size={16} />} onClick={exportHandlers.open} variant="default">
              Dışa Aktar
            </Button>
          </Group>
        }
      />

      <Tabs defaultValue="imports">
        <Tabs.List>
          <Tabs.Tab value="imports">İçe Aktarmalar</Tabs.Tab>
          <Tabs.Tab value="exports">Dışa Aktarmalar</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="imports" pt="md">
          <ImportJobsList />
        </Tabs.Panel>
        <Tabs.Panel value="exports" pt="md">
          <ExportJobsList />
        </Tabs.Panel>
      </Tabs>

      <ImportModal opened={importOpen} onClose={importHandlers.close} />
      <ExportModal opened={exportOpen} onClose={exportHandlers.close} />
    </Stack>
  );
}

// İlerleyen import varsa 5 sn (progress bar canlı aksın), tümü terminal
// state'teyse 60 sn — kullanıcı yeni import başlatmadan liste değişmez.
function ImportJobsList() {
  const { data, isLoading } = useResource<any>("import-jobs", {
    refetchInterval: (query) => {
      const list: any[] = query.state.data?.results || query.state.data || [];
      const hasActive = list.some((j: any) =>
        ["PENDING", "RUNNING", "PROCESSING", "QUEUED"].includes(j.status),
      );
      return hasActive ? 5000 : 60000;
    },
  });
  const retry = useAction("import-jobs", "retry");
  const [errorsOpen, setErrorsOpen] = useState<string | null>(null);
  const list: any[] = data?.results || data || [];

  if (isLoading) return <Center h={200}><Loader /></Center>;
  if (list.length === 0) return <EmptyState title="Import yok" description="Excel/CSV dosyası yükleyerek toplu veri ekleyin." />;

  return (
    <Card padding="sm" radius="md" withBorder>
      <Table.ScrollContainer minWidth={760}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tarih</Table.Th>
            <Table.Th>İmporter</Table.Th>
            <Table.Th>Dosya</Table.Th>
            <Table.Th>İlerleme</Table.Th>
            <Table.Th ta="right">Başarı / Hata</Table.Th>
            <Table.Th>Durum</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {list.map((j: any) => (
            <Table.Tr key={j.id}>
              <Table.Td>{formatDateTime(j.created_at)}</Table.Td>
              <Table.Td><Badge variant="light">{j.importer_name}</Badge></Table.Td>
              <Table.Td><Text size="xs">{j.file_name}</Text></Table.Td>
              <Table.Td style={{ minWidth: 120 }}>
                <Progress value={j.progress_percent || 0} size="sm" />
                <Text size="xs" c="dimmed" ta="center">{j.processed_rows}/{j.total_rows}</Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm" c="green" fw={600}>+{j.success_rows}</Text>
                {j.error_rows > 0 && <Text size="sm" c="red" fw={600}>−{j.error_rows}</Text>}
              </Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[j.status]}>{j.status}</Badge>
                {j.dry_run && <Badge ml={4} color="yellow" size="sm">dry-run</Badge>}
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {j.error_rows > 0 && (
                    <Button size="xs" variant="light" color="red" leftSection={<IconAlertTriangle size={12} />} onClick={() => setErrorsOpen(j.id)}>
                      Hatalar
                    </Button>
                  )}
                  {j.status === "FAILED" && (
                    <Button size="xs" variant="default" leftSection={<IconRefresh size={12} />} onClick={() => retry.mutateAsync({ id: j.id })}>
                      Tekrarla
                    </Button>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>

      <ErrorsModal jobId={errorsOpen} onClose={() => setErrorsOpen(null)} />
    </Card>
  );
}

// Aktif export varsa 5 sn, yoksa 60 sn — bk. ImportJobsList yorumu.
function ExportJobsList() {
  const { data, isLoading } = useResource<any>("export-jobs", {
    refetchInterval: (query) => {
      const list: any[] = query.state.data?.results || query.state.data || [];
      const hasActive = list.some((j: any) =>
        ["PENDING", "RUNNING", "PROCESSING", "QUEUED"].includes(j.status),
      );
      return hasActive ? 5000 : 60000;
    },
  });
  const list: any[] = data?.results || data || [];

  if (isLoading) return <Center h={200}><Loader /></Center>;
  if (list.length === 0) return <EmptyState title="Export yok" />;

  return (
    <Card padding="sm" radius="md" withBorder>
      <Table.ScrollContainer minWidth={560}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tarih</Table.Th>
            <Table.Th>Exporter</Table.Th>
            <Table.Th>Format</Table.Th>
            <Table.Th>Durum</Table.Th>
            <Table.Th>İndir</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {list.map((j: any) => (
            <Table.Tr key={j.id}>
              <Table.Td>{formatDateTime(j.created_at)}</Table.Td>
              <Table.Td><Badge variant="light">{j.exporter_name}</Badge></Table.Td>
              <Table.Td>{j.file_format}</Table.Td>
              <Table.Td><Badge color={STATUS_COLOR[j.status]}>{j.status}</Badge></Table.Td>
              <Table.Td>
                {j.file_url && (
                  <Button size="xs" component="a" href={j.file_url} download leftSection={<IconDownload size={12} />}>
                    İndir
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

function ImportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [importer, setImporter] = useState<string | null>("catalog.product");
  const [format, setFormat] = useState<string>("CSV");
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      // Faz 1: file_name + format kaydı yarat (file_url MinIO upload sonra)
      return (await apiClient.post("/api/v1/import-jobs/", {
        importer_name: importer,
        file_name: file?.name || "manual.csv",
        file_size: file?.size || 0,
        file_format: format,
        dry_run: dryRun,
        options: {},
      })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["import-jobs"] });
      notifications.show({ message: "İçe aktarma başlatıldı (arka planda işleniyor)", color: "green" });
      onClose();
      setFile(null);
    },
    onError: (e: any) => notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Toplu İçe Aktar" size="md">
      <Stack gap="md">
        <Select label="Veri Türü" required data={IMPORTER_OPTIONS} value={importer} onChange={setImporter} />
        <Select label="Dosya Formatı" required data={["CSV", "XLSX", "JSON"].map((v) => ({ value: v, label: v }))} value={format} onChange={(v) => v && setFormat(v)} />
        <FileInput label="Dosya" placeholder="CSV / Excel dosyası seçin" value={file} onChange={setFile} accept=".csv,.xlsx,.json" />
        <Switch label="Dry-run (sadece validasyon, kayıt yapma)" checked={dryRun} onChange={(e) => setDryRun(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>Başlat</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ExportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [exporter, setExporter] = useState<string | null>("catalog.product");
  const [format, setFormat] = useState<string>("XLSX");

  const mutation = useMutation({
    mutationFn: async () => (await apiClient.post("/api/v1/export-jobs/", {
      exporter_name: exporter,
      file_format: format,
      options: {},
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["export-jobs"] });
      notifications.show({ message: "Dışa aktarma hazırlanıyor", color: "green" });
      onClose();
    },
    onError: (e: any) => notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Toplu Dışa Aktar" size="md">
      <Stack gap="md">
        <Select label="Veri Türü" required data={EXPORTER_OPTIONS} value={exporter} onChange={setExporter} />
        <Select label="Format" required data={["CSV", "XLSX", "JSON"].map((v) => ({ value: v, label: v }))} value={format} onChange={(v) => v && setFormat(v)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>Başlat</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ErrorsModal({ jobId, onClose }: { jobId: string | null; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ["import-errors", jobId],
    queryFn: async () => (await apiClient.get(`/api/v1/import-jobs/${jobId}/errors/`)).data,
    enabled: !!jobId,
  });
  const errors: any[] = data || [];
  return (
    <Modal opened={!!jobId} onClose={onClose} title="İçe Aktarma Hataları" size="lg">
      <ScrollArea h={400}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Satır</Table.Th>
              <Table.Th>Alan</Table.Th>
              <Table.Th>Hata</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {errors.map((e: any) => (
              <Table.Tr key={e.id}>
                <Table.Td>{e.row_number}</Table.Td>
                <Table.Td>{e.field || "-"}</Table.Td>
                <Table.Td c="red">{e.message}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Modal>
  );
}
